import { Button } from '@trackman/web-shared-components';
import { ServiceTypes } from 'constants/ServiceTypes';
import { PitchingProgressState } from 'enums/PitchingProgressState';
import { SessionMode } from 'enums/SessionMode';
import { SessionType } from 'enums/SessionType';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { useInjection } from 'services/ioc/ioc.react';
import { ContextStore } from 'stores/ContextStore';
import { FieldCalibrationStore, MAX_CALIBRATION_COUNT_NUMBER, MAX_HOME_PLATE_COUNT_NUMBER } from 'stores/FieldCalibrationStore';
import { NotificationStore } from 'stores/NotificationStore';
import { SessionStore } from 'stores/SessionStore';
import WizardStore from 'stores/WizardStore';
import { RightSide, StyledButton, StyledFooter, StyledFooterContent } from './styles/WizardButtonBarStyles';
import WizardSteps from './WizardSteps';

const WizardButtonBar: React.FC = observer(() => {
  const wizardStore = useInjection<WizardStore>(ServiceTypes.WizardStore);
  const sessionStore = useInjection<SessionStore>(ServiceTypes.SessionStore);
  const contextStore = useInjection<ContextStore>(ServiceTypes.ContextStore);
  const fieldCalibrationStore = useInjection<FieldCalibrationStore>(ServiceTypes.FieldCalibrationStore);
  const notificationStore = useInjection<NotificationStore>(ServiceTypes.NotificationStore);
  const history = useHistory();

  const startBouncingBallCalibrationSession = async (): Promise<void> => {
    if (!contextStore.activeField) {
      notificationStore.addNotification({
        id: 'no-active-field',
        message: 'No active field set.',
        variant: 'error',
        removable: true,
        onClose: () => {
          notificationStore.removeNotification('no-active-field');
        },
      });
    }

    sessionStore.setIsOperatorlessTaggingEnabledForSelectedSession(false);

    const isSuccess = await sessionStore.setCurrentSessionMode({
      sessionMode: SessionMode.SessionStarted,
      sessionType: SessionType.BouncingBallCalibration,
      systemId: contextStore.activeField!.systemId,
      time: '', // will be set in store anyway
      isVideoCaptureEnabled: false,
      isPlayerTrackingEnabled: false,
      privateSession: true,
      externalSessionId: '',
      issuer: 'TaggingApp',
    });
    if (!isSuccess) {
      notificationStore.addNotification({
        id: 'bouncing-ball-calibration-error',
        message:
          'There was an error with stating Bouncing Ball Calibration session. Please go back to main screen and start over. Remember, that running the app in multiple tabs at the same time is not supported.',
        variant: 'error',
        removable: true,
        onClose: () => {
          notificationStore.removeNotification('bouncing-ball-calibration-error');
        },
      });

      return;
    }
    const isSuccessPlayingStarted = await sessionStore.setCurrentSessionMode({
      ...sessionStore.currentSessionMode,
      sessionMode: SessionMode.PlayingStarted,
      issuer: 'TaggingApp',
    });
    if (!isSuccessPlayingStarted) {
      notificationStore.addNotification({
        id: 'playing-started-error',
        message: 'Cannot set PlayingStarted mode for the calibration session',
        variant: 'error',
        removable: true,
        onClose: () => {
          notificationStore.removeNotification('playing-started-error');
        },
      });
    }
  };

  const onNextHandler = () => {
    switch (wizardStore.currentStep) {
      case WizardSteps.CorrectPitching:
        fieldCalibrationStore.setPitchingProgressState(PitchingProgressState.StartPitching);
        startBouncingBallCalibrationSession();
        break;
      case WizardSteps.DataCapture:
        fieldCalibrationStore.setCalibrationConfirmModalVisible(true);
        break;
    }

    wizardStore.next();
  };

  const onAbortHandler = () => {
    fieldCalibrationStore.setAbortModalVisible(true);
  };

  const onBackHandler = () => {
    if (wizardStore.currentStep > WizardSteps.Introduction) {
      return wizardStore.previous();
    }

    history.push('/');
  };

  const getNextButton = () => {
    switch (wizardStore.currentStep) {
      case WizardSteps.Introduction:
        return {
          label: 'Next',
          testAttr: 'begin-button',
        };
      case WizardSteps.CleanHomeplate:
        return {
          label: 'Next',
          testAttr: 'i-understand-button',
        };
      case WizardSteps.CorrectPitching:
        return {
          label: 'Begin',
          testAttr: 'start-data-capture-button',
        };
      case WizardSteps.DataCapture:
        return {
          label: 'Finish',
          testAttr: 'end-capture-button',
          disabled:
            fieldCalibrationStore.bounceWithin5FeetFromHPCount < MAX_CALIBRATION_COUNT_NUMBER &&
            fieldCalibrationStore.hitHPCount < MAX_HOME_PLATE_COUNT_NUMBER,
        };
    }
  };

  const isBackVisible = () => {
    return wizardStore.currentStep < WizardSteps.DataCapture;
  };

  const isAbortVisible = () => {
    return wizardStore.currentStep === WizardSteps.DataCapture;
  };

  return (
    <StyledFooter>
      <StyledFooterContent>
        {isBackVisible() && <StyledButton label='Back' intent='tertiary' onClick={onBackHandler} />}
        <RightSide>
          {isAbortVisible() && (
            <Button label='Abort' intent='tertiary' onClick={onAbortHandler} data-test={getNextButton().testAttr} width={'fit-content'} />
          )}
          <Button
            label={getNextButton().label}
            intent='primary'
            onClick={onNextHandler}
            data-test={getNextButton().testAttr}
            // disabled={getNextButton().disabled}
          />
        </RightSide>
      </StyledFooterContent>
    </StyledFooter>
  );
});

export default WizardButtonBar;
